import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { cn } from '../../ui/utils';
import { toast } from 'sonner';
import { CompanyProvisioningData, provisionNewCompany } from '../../../utils/firebase/companyProvisioning';

interface CreateCompanyDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onCompanyCreated: () => void;
}

export function CreateCompanyDialog({ isOpen, onClose, onCompanyCreated }: CreateCompanyDialogProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<CompanyProvisioningData>({
        companyName: '',
        subdomain: '',
        contactEmail: '',
        contactName: '',
        adminPassword: '',
        phone: '',
        primaryColor: '#3B82F6',
        secondaryColor: '#10B981',
        wellCount: 0,
    });

    const handleChange = (field: keyof CompanyProvisioningData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Auto-generate subdomain from company name
        if (field === 'companyName' && !formData.subdomain) {
            setFormData(prev => ({
                ...prev,
                subdomain: value.toLowerCase().replace(/[^a-z0-9]/g, '-')
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.companyName || !formData.subdomain || !formData.contactEmail || !formData.adminPassword) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (formData.adminPassword.length < 6) {
            toast.error('Admin password must be at least 6 characters');
            return;
        }

        try {
            setIsLoading(true);
            const result = await provisionNewCompany(formData);

            toast.success(`Company ${formData.companyName} created!`, {
                description: `Admin account: ${formData.contactEmail}`
            });

            onCompanyCreated();
            onClose();

            // Reset form
            setFormData({
                companyName: '',
                subdomain: '',
                contactEmail: '',
                contactName: '',
                adminPassword: '',
                phone: '',
                primaryColor: '#3B82F6',
                secondaryColor: '#10B981',
                wellCount: 0,
            });

        } catch (error: any) {
            console.error('Failed to create company:', error);
            toast.error(error.message || 'Failed to create company');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Provision New Company</DialogTitle>
                    <DialogDescription>
                        Create a new tenant with isolated resources and an admin account.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="companyName">Company Name *</Label>
                            <Input
                                id="companyName"
                                placeholder="e.g. Acme Oilfield"
                                value={formData.companyName}
                                onChange={(e) => handleChange('companyName', e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="subdomain">Subdomain (Tenant ID) *</Label>
                            <Input
                                id="subdomain"
                                placeholder="e.g. acme-oil"
                                value={formData.subdomain}
                                onChange={(e) => handleChange('subdomain', e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="contactName">Admin Name *</Label>
                            <Input
                                id="contactName"
                                placeholder="e.g. John Doe"
                                value={formData.contactName}
                                onChange={(e) => handleChange('contactName', e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                                id="phone"
                                placeholder="e.g. 555-0123"
                                value={formData.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="contactEmail">Admin Email *</Label>
                            <Input
                                id="contactEmail"
                                type="email"
                                placeholder="admin@company.com"
                                value={formData.contactEmail}
                                onChange={(e) => handleChange('contactEmail', e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="adminPassword">Admin Password *</Label>
                            <Input
                                id="adminPassword"
                                type="text"
                                placeholder="Sensitive information - min 6 chars"
                                value={formData.adminPassword}
                                onChange={(e) => handleChange('adminPassword', e.target.value)}
                                disabled={isLoading}
                            />
                            <p className="text-[10px] text-muted-foreground">Visible for initial setup. Share securely.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="primaryColor">Primary Color</Label>
                            <div className="flex gap-2">
                                <Input
                                    id="primaryColor"
                                    type="color"
                                    className="w-12 h-10 p-1 cursor-pointer"
                                    value={formData.primaryColor}
                                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                                    disabled={isLoading}
                                />
                                <Input
                                    value={formData.primaryColor}
                                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                                    className="flex-1 font-mono uppercase"
                                    maxLength={7}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="secondaryColor">Secondary Color</Label>
                            <div className="flex gap-2">
                                <Input
                                    id="secondaryColor"
                                    type="color"
                                    className="w-12 h-10 p-1 cursor-pointer"
                                    value={formData.secondaryColor}
                                    onChange={(e) => handleChange('secondaryColor', e.target.value)}
                                    disabled={isLoading}
                                />
                                <Input
                                    value={formData.secondaryColor}
                                    onChange={(e) => handleChange('secondaryColor', e.target.value)}
                                    className="flex-1 font-mono uppercase"
                                    maxLength={7}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="wellCount">Estimated Wells</Label>
                        <Input
                            id="wellCount"
                            type="number"
                            min="0"
                            value={formData.wellCount}
                            onChange={(e) => handleChange('wellCount', parseInt(e.target.value) || 0)}
                            disabled={isLoading}
                        />
                    </div>

                    <DialogFooter className="pt-4">
                        <Button variant="outline" type="button" onClick={onClose} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading} className="bg-blue-600 hover:bg-blue-700 text-white">
                            {isLoading ? 'Provisioning...' : 'Provision Company'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
